import {
    createFeatureSelector,
    createSelector,
    DefaultProjectorFn,
    MemoizedSelector,
} from "@ngrx/store";
import flatMap from "lodash-es/flatMap";

import { MIN_HIGHLIGHTS_LENGTH } from "../constants/grid.constant";
import { getSelectedFilters } from "../helpers/criterias-helper/criterias.helper";
import {
    isSwitchFilter,
    isSwitchFilterActive,
} from "../helpers/switch-filter.helper";
import {
    Facet,
    GridCategoryMetas,
    GridEdito,
    GridFilter,
    GridFilterGroup,
    GridProduct,
    GridType,
    PageType,
    PriceRange,
    SelectedFilter,
} from "../model/grid.model";
import * as fromGrid from "../reducers/grid.reducer";

export const selectGridState = createFeatureSelector<fromGrid.GridState>(
    fromGrid.gridFeatureKey,
);

export const selectUpdateUrlFacet = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => ({
        fhParams: gridState.urlParams,
        sort: gridState.sortBy,
        facet:
            gridState.gridType === GridType.facet
                ? gridState.facet
                : fromGrid.initialState.facet,
        searchTerm: gridState.searchTerm,
    }),
);

export const selectCategory = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.category,
);

export const selectCategoryMetas = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.categoryMetas,
);

export const selectSeoTextOnCategoryMetas = createSelector(
    selectCategoryMetas,
    (categoryMeta: GridCategoryMetas | undefined) => {
        if (!categoryMeta?.textOnGrid) {
            return undefined;
        }

        return categoryMeta.textOnGrid;
    },
);

export const selectPendingCategoryMetas = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.pendingCategoryMetas,
);

export const selectMenuLevel = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.menuLevel,
);

export const selectEditos = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.editos,
);

export const selectProducts = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.products,
);

export const selectIsFromPreviousGrid = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.isFromPreviousGrid,
);

export const selectAlternatives = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.alternatives,
);

export const selectPriceRange = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.prices,
);

export const selectPositionInPage = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.positionInPage,
);

export const selectFilterGroups = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.filterGroups,
);

export const selectFilterGroupsWithPrices = createSelector(
    selectFilterGroups,
    selectPriceRange,
    (
        filterGroups: GridFilterGroup[] | undefined,
        prices: PriceRange | undefined,
    ) => {
        if (!filterGroups) {
            return [];
        }

        return filterGroups.map((gridFilterGroup: GridFilterGroup) => {
            const filterGroup = { ...gridFilterGroup };
            if (filterGroup.presentation !== "slider") {
                return filterGroup;
            }
            // provide priceRange for price filter
            filterGroup.priceRange = prices;
            return filterGroup;
        });
    },
);

export const selectIsPriceSelected = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) =>
        !!gridState.prices &&
        (gridState.prices.valueMinCurrent !== gridState.prices.valueMin ||
            gridState.prices.valueMaxCurrent !== gridState.prices.valueMax),
);

export const selectLoadMore = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.loadMore,
);

export const selectPage = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.page,
);

export const selectPageNumber = createSelector(
    selectPage,
    (page: {
        pageNumber: number;
        pagesLoaded: number;
        previousPage: PageType;
    }) => page.pageNumber,
);

export const selectPagesLoaded = createSelector(
    selectPage,
    (page: {
        pageNumber: number;
        pagesLoaded: number;
        previousPage: PageType;
    }) => page.pagesLoaded,
);

export const selectPreviousPage = createSelector(
    selectPage,
    (page: {
        pageNumber: number;
        pagesLoaded: number;
        previousPage: PageType;
    }) => page.previousPage,
);

export const selectTotal = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.total,
);

export const selectPendingResultItems = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.pendingGridDatas,
);

export const selectResultItems = createSelector(
    selectProducts,
    selectEditos,
    (products: GridProduct[] | undefined, editos: GridEdito[] | undefined) => {
        if (!editos || !products) {
            return undefined;
        }
        return {
            products,
            editos,
        };
    },
);

export const selectDisplayMode = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.displayMode,
);

export const selectSortBy = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.sortBy,
);

export const selectCriterias = createSelector(
    selectGridState,
    selectPage,
    (
        gridState: fromGrid.GridState,
        page: {
            pageNumber: number;
            pagesLoaded: number;
        },
    ) => ({
        pimCode: gridState.category?.pimCode,
        searchTerm: gridState.searchTerm,
        urlParams: gridState.urlParams,
        sort: gridState.sortBy,
        prices: gridState.prices,
        pageNumber: page.pageNumber,
        pagesLoaded: page.pagesLoaded,
    }),
);

export const selectSelectedFilters = createSelector(
    selectFilterGroupsWithPrices,
    selectIsPriceSelected,
    selectPriceRange,
    (
        filterGroups: GridFilterGroup[],
        isPriceSelected: boolean,
        prices: PriceRange | undefined,
    ) => {
        const selectedFilters: SelectedFilter[] =
            getSelectedFilters(filterGroups);

        if (isPriceSelected && prices) {
            selectedFilters.push({
                name: "prices",
                value: `${prices.valueMin} - ${prices.valueMax}`,
            });
        }
        return selectedFilters;
    },
);

export const selectHasSelectedFilters = createSelector(
    selectSelectedFilters,
    (selectedFilters) =>
        selectedFilters !== undefined && selectedFilters.length > 0,
);

export const selectNbFiltersSelected = createSelector(
    selectFilterGroups,
    (filterGroups) => {
        let nbFiltersSelected = 0;
        filterGroups.forEach((filterGroup) => {
            if (isSwitchFilter(filterGroup)) {
                // Switch filters may count as 0 (if inactive) or 1 (if active)
                nbFiltersSelected += isSwitchFilterActive(filterGroup) ? 1 : 0;
            } else {
                nbFiltersSelected += filterGroup.filters?.reduce(
                    (accumulator, filter) =>
                        filter.selected ? accumulator + 1 : accumulator,
                    0,
                );
            }
        });
        return nbFiltersSelected;
    },
);

export const selectFacet = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.facet,
);

export const selectGridHeader = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => ({
        collectionSize: gridState.total,
        facetDisplayName: gridState.facet.facetDisplayName,
        currentCategory: gridState.category,
        hideTotalUpdatedItems: gridState.total === undefined,
    }),
);

export const selectUrl = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.url,
);

export const selectGridType = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.gridType,
);

export const selectMissingFacet = createSelector(
    selectFacet,
    (facet: Facet) => facet?.missingFacet,
);

export const selectIsSearchGrid = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.gridType === GridType.search,
);

export const selectCountSelectedFilter = (
    filterGroupName: string,
    // eslint-disable-next-line @typescript-eslint/ban-types
): MemoizedSelector<object, number, DefaultProjectorFn<number>> =>
    createSelector(
        selectFilterGroupsWithPrices,
        selectIsPriceSelected,
        (filterGroups: GridFilterGroup[], isPriceSelected: boolean) => {
            const filterGroup = filterGroups.find(
                (filter) => filter.name === filterGroupName,
            );

            if (filterGroup?.presentation === "slider" && isPriceSelected) {
                return 1;
            }
            if (filterGroup?.filters) {
                return filterGroup.filters.filter((filter) => filter.selected)
                    .length;
            }
            return 0;
        },
    );

export const selectSwitchFilterActive = (
    filterGroupName: string,
): MemoizedSelector<object, boolean, DefaultProjectorFn<boolean>> =>
    createSelector(
        selectFilterGroupsWithPrices,
        (filterGroups: GridFilterGroup[]) =>
            filterGroups.some(
                (filter) =>
                    filter.name === filterGroupName &&
                    isSwitchFilterActive(filter),
            ),
    );

export const selectIsHeroProduct = createSelector(
    selectGridState,
    (gridState: fromGrid.GridState) => gridState.isHeroProduct,
);

export const selectGridHighlightsFilterGroups = createSelector(
    selectFilterGroups,
    (filterGroups: GridFilterGroup[] | undefined) =>
        filterGroups?.filter(
            (filterGroup: GridFilterGroup) => filterGroup.isGridHighlight,
        ),
);

export const selectGridHighlights = createSelector(
    selectGridHighlightsFilterGroups,
    (filterGroups: GridFilterGroup[] | undefined) =>
        flatMap(
            filterGroups?.map((filterGroup: GridFilterGroup) =>
                filterGroup.filters
                    ?.map((filter: GridFilter) => filter)
                    .filter(Boolean),
            ),
        ),
);

// TODO: Remove the selector when FF 'grid_highlight_new_design' is enabled
export const selectIsGridHighlighsEnabled = createSelector(
    selectHasSelectedFilters,
    selectGridState,
    selectGridHighlights,
    (
        hasSelectedFilters: boolean,
        gridState: fromGrid.GridState,
        gridHighlights: GridFilter[],
    ) =>
        !hasSelectedFilters &&
        gridState.gridType !== GridType.search &&
        gridHighlights.length >= MIN_HIGHLIGHTS_LENGTH,
);

export const selectIsGridHighlighsRevampEnabled = createSelector(
    selectHasSelectedFilters,
    selectGridState,
    selectGridHighlights,
    (
        hasSelectedFilters: boolean,
        gridState: fromGrid.GridState,
        gridHighlights: GridFilter[],
    ) =>
        !hasSelectedFilters &&
        gridState.gridType !== GridType.search &&
        gridHighlights.length >= 3,
);
